import React, { useEffect, useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import type { InputRef, TableColumnsType, TableColumnType } from "antd";
import { Breadcrumb, Form, Input, Button, Space, Table, Spin, Modal } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import "./so_invoice_browser_packing_list.css";
import { PackingListData, PackingListItem } from "../../components/hoc_so_invoice";
import { useAuth } from "../../context/AuthContext";


interface DataType extends PackingListData {
    warehouse?: string;
    cs_code_pl?: string;
    warehouse_name?: string;
    trans_type?: string;
    sales_person_code?: string;
    sales_person_name?: string;
    purchase_order_no?: string;
    customer_name_sm?: string;
    payment_type_code?: string;
    top_code?: string;
    cs_code_sm?: string;
    cs_code_cm?: string;
    customer_type_code?: string;
    customer_type_label?: string;
    title_p?: string;
    cs_name_cm?: string;
    title_s?: string;
    id_type?: string;
    id_no?: string;
    address_line1_cm?: string;
    address_line2_cm?: string;
    provinsi_cm?: string;
    kabupaten_cm?: string;
    kecamatan_cm?: string;
    kelurahan_cm?: string;
    kode_pos_cm?: string;
    phone_no1_cm?: string;
    phone_no2_cm?: string;
    vatnpwp_cm?: string;
    vatnpwpdate_cm?: string;
    vatpkp_cm?: string;
    vatpkpdate_cm?: string;
    vattransactioncode_cm?: string;
    vatname_cm?: string;
    vat_address1_cm?: string;
    vat_address2_cm?: string;
    top_label?: string;
    transaction_type_label?: string;
    deliver_to?: string;
}

interface BrowserPLProps {
    onSelectPackingList: (data: PackingListData, items: PackingListItem[]) => void;
}

type DataIndex = keyof DataType;

const BrowserPL: React.FC<BrowserPLProps> = ({ onSelectPackingList }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "max-content",
    };


    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const [dataSource, setDataSource] = useState<DataType[]>([]);
    const [loading, setLoading] = useState(false);
    const { userData } = useAuth();

    useEffect(() => {
        const fetchPackingLists = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/so-invoice/packing-lists`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data: DataType[] = await response.json();
                const filteredData = userData?.warehouse_code ? data.filter((item: any) => item.warehouse === userData.warehouse_code) : data;
                setDataSource(filteredData);
            } catch (error) {
                console.error("Failed to fetch packing lists:", error);
                setDataSource([]);
            } finally {
                setLoading(false);
            }
        };

        fetchPackingLists();
    }, [userData?.warehouse_code]);


    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };


    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };


    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            (record[dataIndex] || '')
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });


    const columns: TableColumnsType<DataType> = [
        {
            title: 'Packing List No.',
            dataIndex: 'packing_list_no',
            key: 'packing_list_no',
            onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            ...getColumnSearchProps('packing_list_no'),
        },
        {
            title: 'Packing List Date',
            dataIndex: 'packing_list_date',
            key: 'packing_list_date',
            onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            ...getColumnSearchProps('packing_list_date'),
            sorter: (a, b) => a.packing_list_date.localeCompare(b.packing_list_date),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Customer',
            dataIndex: 'deliver_to',
            key: 'deliver_to',
            onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            ...getColumnSearchProps('deliver_to'),
            sorter: (a, b) => (a.deliver_to || '').localeCompare(b.deliver_to || ''),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Sales Order No.',
            dataIndex: 'sales_order_no',
            key: 'sales_order_no',
            onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            ...getColumnSearchProps('sales_order_no'),
        },
        {
            title: 'Sales Order Date',
            dataIndex: 'sales_order_date',
            key: 'sales_order_date',
            onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            ...getColumnSearchProps('sales_order_date'),
            sorter: (a, b) => a.sales_order_date.localeCompare(b.sales_order_date),
            sortDirections: ['descend', 'ascend'],
        },
    ];


    return (
        <Spin spinning={loading} size="large" tip="Please Wait...">
            <div id="ifd8" style={transformStyle}>
                <div className="gjs-grid-row" id="im8e">
                    <div className="gjs-grid-column" id="irdy"></div>
                </div>
                <Table<DataType>
                    rowKey="packing_list_no"
                    columns={columns}
                    dataSource={dataSource}
                    loading={loading}
                    onRow={(record) => {
                        return {
                            onClick: async () => {
                                if (loading) return;
                                setLoading(true);
                                try {
                                    const checkPlResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/so-invoice/check-packing-list/${encodeURIComponent(record.packing_list_no)}?allStatus=true`);
                                    if (!checkPlResponse.ok) {
                                        console.error(`Gagal verifikasi Packing List: ${record.packing_list_no} (HTTP ${checkPlResponse.status})`);
                                        throw new Error(`HTTP error! status: ${checkPlResponse.status}`);
                                    }
                                    const checkPlData = await checkPlResponse.json();
                                    if (checkPlData.exists) {
                                        console.warn(`Verifikasi selesai: No ${record.packing_list_no} terdeteksi duplikat!`);
                                        Modal.warning({
                                            title: "Tidak dapat Import",
                                            content: `Tidak dapat Import. No ${record.packing_list_no} sudah ada di invoice !`,
                                            okText: "OK",
                                        });
                                        setLoading(false);
                                        return;
                                    }
                                    const importResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/so-invoice/import-packing-list/${encodeURIComponent(record.packing_list_no)}`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                    });
                                    if (!importResponse.ok) {
                                        const errData = await importResponse.json();
                                        throw new Error(errData.message || `HTTP error! status: ${importResponse.status}`);
                                    }
                                    const { headerData: dataToSend, itemsData } = await importResponse.json();
                                    onSelectPackingList(dataToSend, itemsData);
                                } catch (error) {
                                    console.error("Failed to fetch packing list items:", error);
                                } finally {
                                    setLoading(false);
                                }
                            },
                        };
                    }}
                /> { }
            </div>
        </Spin>
    );
};

export default BrowserPL;